// Cross-domain auth bridge: transfers tokens from novelday.cc to novelday.net.
//
// localStorage is per-origin, so tokens stored on novelday.cc are inaccessible
// on novelday.net. This module embeds a hidden iframe pointing to
// novelday.cc/auth-bridge.html, which reads tokens from .cc's localStorage
// and sends them via postMessage. We then store them on .net.
//
// This handles the case where a user logged in on novelday.cc before the 308
// redirect was added, or if Cloudflare/CDN caching served the SPA on .cc.

import { getStoredAuthToken, setStoredAuthToken, setStoredRefreshToken } from './authStorage';

const BRIDGE_URL = 'https://novelday.cc/auth-bridge.html';
const BRIDGE_TIMEOUT_MS = 5000;
const STORAGE_KEY = 'novelday:cross-domain-transfer-attempted';

let transferAttempted = false;

// Test-only helper to reset the module-level flag (prevents cross-test interference).
// Mirrors the pattern used by authEvents._clearListeners.
export const _resetTransferAttempted = () => { transferAttempted = false; };

export const attemptCrossDomainTransfer = () => {
  return new Promise((resolve) => {
    // Only run once per page load, and only if we don't have a token on .net.
    if (transferAttempted) {
      resolve(false);
      return;
    }

    // Skip if we already have a token on .net — no need to transfer.
    if (getStoredAuthToken()) {
      transferAttempted = true;
      resolve(false);
      return;
    }

    // Skip if we already attempted a transfer recently (sessionStorage to
    // avoid retrying on every navigation within the same session).
    try {
      if (sessionStorage.getItem(STORAGE_KEY)) {
        resolve(false);
        return;
      }
    } catch (_e) {}

    transferAttempted = true;
    try { sessionStorage.setItem(STORAGE_KEY, '1'); } catch (_e) {}

    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = BRIDGE_URL;

    let resolved = false;
    const cleanup = () => {
      if (iframe.parentNode) {
        iframe.parentNode.removeChild(iframe);
      }
      window.removeEventListener('message', handleMessage);
    };

    const timeout = setTimeout(() => {
      if (!resolved) {
        resolved = true;
        cleanup();
        resolve(false);
      }
    }, BRIDGE_TIMEOUT_MS);

    const handleMessage = (event) => {
      if (event.origin !== 'https://novelday.cc') return;
      if (!event.data || event.data.type !== 'auth-bridge') return;

      resolved = true;
      clearTimeout(timeout);
      cleanup();

      if (event.data.empty) {
        resolve(false);
        return;
      }

      let transferred = false;
      if (event.data.token) {
        setStoredAuthToken(event.data.token);
        transferred = true;
      }
      if (event.data.refreshToken) {
        setStoredRefreshToken(event.data.refreshToken);
      }

      resolve(transferred);
    };

    window.addEventListener('message', handleMessage);
    document.body.appendChild(iframe);
  });
};
